import { zodResolver } from '@hookform/resolvers/zod';
import { isFixedProduct, isPromotionProduct, isUsageProduct, unsetProductDatesProcessor } from '@vy/services';
import type { FixedProductInput, UsageProductInput } from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ErrorLabel } from '../../../../../components/design/ErrorLabel';
import { withSuspenseBoundary } from '../../../../../components/utility/SuspenseBoundary';
import { usePlanById } from '../../../../../hooks/requests/use-plan-by-id';
import { useValidatedParams } from '../../../../../hooks/use-validated-params';
import { getProRataSetting } from '../../../../../services/get-pro-ration-setting-value';
import { processRateMultiplierPlanProducts } from '../../../../../services/rate-multiplier-product-processor';
import type { NewPlanForm } from '../../../CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../../CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';
import { PlanInputFormSchema } from '../../../CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';

const UpdatePlanForm: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { planId } = useValidatedParams('planId');

  const {
    data: { plan },
    isSuccess,
  } = usePlanById(planId);

  const methods = useForm<PlanInputForm, unknown, NewPlanForm>({
    resolver: zodResolver(PlanInputFormSchema),
    defaultValues: plan,
  });

  useEffect(() => {
    if (!isSuccess) {
      return;
    }

    const { products: planProducts, ...planWithoutProducts } = plan;
    const rateAdjustedProducts = processRateMultiplierPlanProducts(planProducts);
    const productsWithoutDates = unsetProductDatesProcessor(rateAdjustedProducts);
    const filteredFixedProductsWithPromotions = productsWithoutDates.filter(
      isFixedProduct,
    ) as FixedProductInput[];
    const filteredFixedProducts = filteredFixedProductsWithPromotions.filter(
      (product) => !isPromotionProduct(product),
    );
    const promotionProducts = filteredFixedProductsWithPromotions.filter((product) =>
      isPromotionProduct(product),
    );
    const filteredUsageProducts = productsWithoutDates.filter(isUsageProduct) as UsageProductInput[];
    methods.reset({
      ...planWithoutProducts,
      proRataSetting: getProRataSetting(plan),
      fixedProducts: filteredFixedProducts,
      usageProducts: filteredUsageProducts,
      promotionProducts,
    });
  }, [isSuccess, plan, methods]);

  return (
    <FormProvider {...methods}>
      <form className="flex h-full flex-col">{children}</form>
      {isNotNullOrUndefined(methods.formState.errors.root) && (
        <ErrorLabel errorMessage={methods.formState.errors.root?.message ?? ''} />
      )}
    </FormProvider>
  );
};

const UpdatePlanPageWithSuspenseBoundary = withSuspenseBoundary(UpdatePlanForm);
export { UpdatePlanPageWithSuspenseBoundary as UpdatePlanForm };
